import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { INVENTORY_ITEM_TYPES, ITEM_CONDITIONS } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { inventoryApi } from '@/services/inventory.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

/**
 * Creates an item.
 *
 * Only name, category and location are required — the same "an honest blank
 * beats a placeholder" reasoning `GroupForm` documents for a leader who does
 * not yet know every detail. `quantityOnHand` itself is never a field here:
 * an opening balance is entered as `initialQuantity`, recorded as an ordinary
 * RECEIPT movement, exactly as arriving stock always is.
 */
export function ItemForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: '',
    categoryId: '',
    locationId: '',
    supplierId: '',
    type: 'CONSUMABLE' as (typeof INVENTORY_ITEM_TYPES)[number],
    condition: 'GOOD' as (typeof ITEM_CONDITIONS)[number],
    unit: 'unidad',
    initialQuantity: '',
    minQuantity: '',
    sku: '',
    serialNumber: '',
    notes: '',
  });

  const categories = useQuery({
    queryKey: ['inventory', 'categories', 'options'],
    queryFn: ({ signal }) => inventoryApi.listCategories({ limit: 100 }, signal),
  });
  const locations = useQuery({
    queryKey: ['inventory', 'locations', 'options'],
    queryFn: ({ signal }) => inventoryApi.listLocations({ limit: 100 }, signal),
  });
  const suppliers = useQuery({
    queryKey: ['inventory', 'suppliers', 'options'],
    queryFn: ({ signal }) => inventoryApi.listSuppliers({ limit: 100 }, signal),
  });

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: () =>
      inventoryApi.createItem({
        name: form.name.trim(),
        categoryId: form.categoryId,
        locationId: form.locationId,
        supplierId: form.supplierId || null,
        type: form.type,
        condition: form.condition,
        unit: form.unit.trim() || 'unidad',
        initialQuantity: form.initialQuantity ? Number(form.initialQuantity) : 0,
        minQuantity: form.minQuantity ? Number(form.minQuantity) : null,
        sku: form.sku.trim() || null,
        serialNumber: form.serialNumber.trim() || null,
        notes: form.notes.trim() || null,
      }),
    onSuccess: onCreated,
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        const fields = error.fieldErrors;
        setFieldErrors(fields);
        if (Object.keys(fields).length === 0) {
          setFormError(t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') }));
        }
        return;
      }
      setFormError(t('errors.INTERNAL_ERROR'));
    },
  });

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);
    mutation.mutate();
  }

  return (
    <dialog ref={dialogRef} onClose={onClose} className="dialog" aria-labelledby="item-form-title">
      <form onSubmit={handleSubmit} noValidate className="dialog-body">
        <h2 id="item-form-title">{t('inventory.item.create')}</h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <Field
          label={t('inventory.item.name')}
          name="name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          error={fieldErrors.name}
          required
          autoFocus
        />

        <div className="form-grid">
          <div className="field">
            <label className="field-label" htmlFor="field-categoryId">
              {t('inventory.category.singular')}
            </label>
            <select
              id="field-categoryId"
              name="categoryId"
              className="field-input"
              value={form.categoryId}
              onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
              required
            >
              <option value="">—</option>
              {(categories.data?.data ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field-label" htmlFor="field-locationId">
              {t('inventory.location.singular')}
            </label>
            <select
              id="field-locationId"
              name="locationId"
              className="field-input"
              value={form.locationId}
              onChange={(e) => setForm({ ...form, locationId: e.target.value })}
              required
            >
              <option value="">—</option>
              {(locations.data?.data ?? []).map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-grid">
          <div className="field">
            <label className="field-label" htmlFor="field-type">
              {t('inventory.item.type')}
            </label>
            <select
              id="field-type"
              name="type"
              className="field-input"
              value={form.type}
              onChange={(e) =>
                setForm({ ...form, type: e.target.value as (typeof INVENTORY_ITEM_TYPES)[number] })
              }
            >
              {INVENTORY_ITEM_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`inventory.type.${type}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field-label" htmlFor="field-condition">
              {t('inventory.item.condition')}
            </label>
            <select
              id="field-condition"
              name="condition"
              className="field-input"
              value={form.condition}
              onChange={(e) =>
                setForm({ ...form, condition: e.target.value as (typeof ITEM_CONDITIONS)[number] })
              }
            >
              {ITEM_CONDITIONS.map((condition) => (
                <option key={condition} value={condition}>
                  {t(`inventory.condition.${condition}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-grid">
          <Field
            label={t('inventory.item.initialQuantity')}
            hint={t('inventory.item.initialQuantityHint')}
            name="initialQuantity"
            type="number"
            min={0}
            value={form.initialQuantity}
            onChange={(e) => setForm({ ...form, initialQuantity: e.target.value })}
            error={fieldErrors.initialQuantity}
            dir="ltr"
          />

          <Field
            label={t('inventory.item.unit')}
            name="unit"
            value={form.unit}
            onChange={(e) => setForm({ ...form, unit: e.target.value })}
            error={fieldErrors.unit}
          />

          <Field
            label={t('inventory.item.minQuantity')}
            hint={t('inventory.item.minQuantityHint')}
            name="minQuantity"
            type="number"
            min={0}
            value={form.minQuantity}
            onChange={(e) => setForm({ ...form, minQuantity: e.target.value })}
            error={fieldErrors.minQuantity}
            dir="ltr"
          />
        </div>

        <div className="form-grid">
          <div className="field">
            <label className="field-label" htmlFor="field-supplierId">
              {t('inventory.supplier.singular')}
            </label>
            <select
              id="field-supplierId"
              name="supplierId"
              className="field-input"
              value={form.supplierId}
              onChange={(e) => setForm({ ...form, supplierId: e.target.value })}
            >
              <option value="">—</option>
              {(suppliers.data?.data ?? []).map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>

          <Field
            label={t('inventory.item.sku')}
            name="sku"
            value={form.sku}
            onChange={(e) => setForm({ ...form, sku: e.target.value })}
            error={fieldErrors.sku}
            dir="ltr"
          />

          <Field
            label={t('inventory.item.serialNumber')}
            name="serialNumber"
            value={form.serialNumber}
            onChange={(e) => setForm({ ...form, serialNumber: e.target.value })}
            error={fieldErrors.serialNumber}
            dir="ltr"
          />
        </div>

        <Field
          label={t('inventory.item.notes')}
          name="notes"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          error={fieldErrors.notes}
        />

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={mutation.isPending}
            disabled={!form.name.trim() || !form.categoryId || !form.locationId}
          >
            {t('common.actions.save')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
